import React, { useMemo } from 'react';

import { createBottomTabNavigator } from '@react-navigation/bottom-tabs';

import { LobbyIcon } from '@/assets/icons/tab/home';
import { VaultIcon } from '@/assets/icons/tab/vault';
import { useRipsInventoryCount } from '@/feature/lobby-rips/hooks/use-rips-inventory-count';
import { useRipsSession } from '@/feature/lobby-rips/hooks/use-rips-session';
import { useRipsTermsCheck } from '@/feature/lobby-rips/hooks/use-rips-terms-check';
import { useAppInit } from '@/hooks/use-app-init';
import { useJurisdictionStore } from '@/hooks/use-jurisdiction';
import { useOneLinkNavigation } from '@/hooks/use-one-link-navigation';

import { AccountTabConfig, TabBar, TabOptions, mapTabsFromTabSettings } from '../tabs';
import { LobbyStack } from './lobbyStack';
import { RipsTabNavigatorParamList } from './types';

const TabRips = createBottomTabNavigator<RipsTabNavigatorParamList>();

// The Vault tab never shows a screen: pressing it opens the Vault in the Rips web app (see `vaultTabListeners`).
const RipsVaultPlaceholder = () => null;

const MAX_BADGE_COUNT = 99;

const formatBadge = (count: number) => {
    if (count <= 0) {
        return undefined;
    }

    return count > MAX_BADGE_COUNT ? `${MAX_BADGE_COUNT}+` : count;
};

const RipsTabsConfig = [
    { name: 'RipsLobby', component: LobbyStack, icon: LobbyIcon, title: 'Home' },
    { name: 'RipsVault', component: RipsVaultPlaceholder, icon: VaultIcon, title: 'Vault' },
] as const;

export const RipsTabsNavigator = () => {
    useOneLinkNavigation();

    const tabSettings = useJurisdictionStore(state => state.jurisdictionSettings?.productConfig?.tabSettings);

    const tabsConfig = useMemo(() => {
        return mapTabsFromTabSettings([...RipsTabsConfig, AccountTabConfig], tabSettings);
    }, [tabSettings]);

    useAppInit();

    const { goToVault } = useRipsSession();
    const checkRipsTerms = useRipsTermsCheck();
    const vaultBadge = formatBadge(useRipsInventoryCount());
    const vaultTabListeners = useMemo(
        () => ({
            tabPress: async (event: { preventDefault: () => void }) => {
                // Stay on the current tab (Vault is never highlighted) and open the Vault web app instead.
                event.preventDefault();
                const hasAcceptedTerms = await checkRipsTerms();
                if (hasAcceptedTerms) {
                    goToVault();
                }
            },
        }),
        [checkRipsTerms, goToVault]
    );

    return (
        <TabRips.Navigator screenOptions={TabOptions} tabBar={TabBar}>
            {tabsConfig.map(tab => (
                <TabRips.Screen
                    key={tab.name}
                    {...tab}
                    options={{
                        tabBarIcon: tab.icon,
                        title: tab.title,
                        tabBarBadge: tab.name === 'RipsVault' ? vaultBadge : undefined,
                    }}
                    listeners={tab.name === 'RipsVault' ? vaultTabListeners : undefined}
                />
            ))}
        </TabRips.Navigator>
    );
};
